import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    timeout: Number,
    warningTime: Number,
    checkInterval: Number,
    testMode: Boolean,
    absoluteRemaining: Number,
    warningEnabled: Boolean
  }

  static targets = ["dialog", "overlay", "countdown", "extendButton", "logoutButton"]

  connect() {
    // Nur im Backend aktivieren
    if (!document.body.classList.contains('backend')) {
      return
    }

    // Verwende die Werte aus den data-Attributen oder Standard-Werte
    this.timeoutValue = this.timeoutValue || 30 * 60 * 1000 // 30 Minuten Standard
    this.warningTimeValue = this.warningTimeValue || 5 * 60 * 1000 // 5 Minuten Warnung
    this.checkIntervalValue = this.checkIntervalValue || 60 * 1000 // 1 Minute Check-Intervall

    this.lastActivity = Date.now()
    this.warningShown = false
    this.sessionExpired = false

    // Absolutes 6h-Limit (ApplicationController): fixe Deadline, bewegt sich NICHT
    // mit Aktivität. Nur aktiv wenn Feature-Toggle an (data-warning-enabled).
    this.absoluteDeadline = (this.warningEnabledValue && this.absoluteRemainingValue > 0)
      ? Date.now() + this.absoluteRemainingValue
      : null
    this.absoluteWarningActive = false

    // Test-Modus: Zeige Debug-Info und verkürzte Zeiten
    if (this.testModeValue) {
      this.setupTestMode()
    }

    this.setupEventListeners()
    this.startSessionCheck()
    this.setupFormProtection()
  }

  setupTestMode() {
    console.log('🧪 Session Timeout Test Mode aktiviert')
    console.log('⏱️  Timeout:', this.timeoutValue / 1000, 'Sekunden')
    console.log('⚠️  Warnung:', this.warningTimeValue / 1000, 'Sekunden vor Ablauf')
    console.log('🔄 Check alle:', this.checkIntervalValue / 1000, 'Sekunden')
    
    // Zeige Test-Info in der UI
    this.showTestModeInfo()
  }

  showTestModeInfo() {
    const testInfo = document.createElement('div')
    testInfo.id = 'session-timeout-test-info'
    testInfo.innerHTML = `
      <div style="position: fixed; top: 10px; right: 10px; background: #fbbf24; color: #92400e; padding: 8px 12px; border-radius: 6px; font-size: 12px; z-index: 9999; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
        🧪 Test-Modus: Session ${this.timeoutValue / 1000}s, Warnung ${this.warningTimeValue / 1000}s
        <button onclick="this.parentElement.parentElement.remove()" style="margin-left: 8px; background: none; border: none; color: #92400e; cursor: pointer;">×</button>
      </div>
    `
    document.body.appendChild(testInfo)
  }

  disconnect() {
    this.clearSessionCheck()
    this.clearWarningTimer()
  }

  setupEventListeners() {
    // Aktivitäts-Events
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    
    activityEvents.forEach(event => {
      document.addEventListener(event, this.resetTimer.bind(this), true)
    })

    // Visibility Change (Tab wechseln)
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this))
    
    // Beforeunload (Seite verlassen)
    window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this))
  }

  setupFormProtection() {
    // Finde alle Backend-Formulare
    this.forms = document.querySelectorAll('.backend form')
    
    this.forms.forEach(form => {
      // Event Listener für Änderungen
      form.addEventListener('input', () => this.markFormAsChanged(form))
      form.addEventListener('change', () => this.markFormAsChanged(form))
    })
  }

  markFormAsChanged(form) {
    if (this.sessionExpired) return
    
    // Markiere Formular als geändert
    form.classList.add('form-changed')
    
    // Speichere Zeitstempel der letzten Änderung
    form.dataset.lastChanged = Date.now()
  }

  resetTimer() {
    this.lastActivity = Date.now()
    // Die Vorwarnung fuer das absolute 6h-Limit darf NICHT durch Aktivität
    // verschwinden — nur "Sitzung verlängern" (extendSession) setzt sie zurueck.
    if (this.absoluteWarningActive) return
    this.warningShown = false

    if (this.warningTimer) {
      clearTimeout(this.warningTimer)
      this.warningTimer = null
    }
  }

  startSessionCheck() {
    this.sessionCheckInterval = setInterval(() => {
      this.checkSessionStatus()
    }, this.checkIntervalValue)
  }

  clearSessionCheck() {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval)
      this.sessionCheckInterval = null
    }
  }

  checkSessionStatus() {
    const now = Date.now()

    // Absolutes 6h-Limit (unabhängig von Aktivität) — nur wenn Feature aktiv.
    if (this.absoluteDeadline) {
      const timeUntilAbsolute = this.absoluteDeadline - now
      if (timeUntilAbsolute <= 0) {
        this.handleSessionExpired()
        return
      }
      if (timeUntilAbsolute <= this.warningTimeValue && !this.warningShown) {
        this.absoluteWarningActive = true
        this.showWarning(timeUntilAbsolute)
        return
      }
    }

    const timeSinceActivity = now - this.lastActivity
    const timeUntilExpiry = this.timeoutValue - timeSinceActivity

    // Session ist abgelaufen
    if (timeSinceActivity >= this.timeoutValue) {
      this.handleSessionExpired()
      return
    }

    // Warnung anzeigen
    if (timeUntilExpiry <= this.warningTimeValue && !this.warningShown) {
      this.showWarning(timeUntilExpiry)
    }
  }

  showWarning(timeUntilExpiry) {
    this.warningShown = true
    this.showDialog()
    this.startCountdown(timeUntilExpiry)
  }

  showDialog() {
    if (this.hasDialogTarget) {
      this.dialogTarget.classList.remove('hidden')
    }
    
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove('hidden')
    }

    // App grau machen
    document.body.classList.add('session-warning-active')
  }

  hideDialog() {
    if (this.hasDialogTarget) {
      this.dialogTarget.classList.add('hidden')
    }
    
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add('hidden')
    }

    // App wieder normal
    document.body.classList.remove('session-warning-active')
  }

  startCountdown(timeUntilExpiry) {
    let remainingTime = Math.ceil(timeUntilExpiry / 1000)
    
    if (this.hasCountdownTarget) {
      this.countdownTarget.textContent = this.formatTime(remainingTime)
    }

    this.countdownInterval = setInterval(() => {
      remainingTime--
      
      if (this.hasCountdownTarget) {
        this.countdownTarget.textContent = this.formatTime(remainingTime)
      }

      if (remainingTime <= 0) {
        this.handleSessionExpired()
      }
    }, 1000)
  }

  clearWarningTimer() {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer)
      this.warningTimer = null
    }
    
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
      this.countdownInterval = null
    }
  }

  formatTime(seconds) {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  handleSessionExpired() {
    this.sessionExpired = true
    this.clearWarningTimer()
    this.clearSessionCheck()
    
    // Zeige Session-Ablauf-Dialog
    this.showSessionExpiredDialog()
  }

  showSessionExpiredDialog() {
    // Erstelle Session-Ablauf-Dialog falls nicht vorhanden
    if (!document.getElementById('session-expired-dialog')) {
      this.createSessionExpiredDialog()
    }
    
    document.getElementById('session-expired-dialog').classList.remove('hidden')
    document.body.classList.add('session-expired-active')
  }

  createSessionExpiredDialog() {
    const dialog = document.createElement('div')
    dialog.id = 'session-expired-dialog'
    dialog.className = 'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'
    dialog.innerHTML = `
      <div class="bg-white rounded-lg p-6 max-w-md mx-4">
        <div class="text-center">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-2">Sitzung abgelaufen</h3>
          <p class="text-sm text-gray-500 mb-4">
            Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an, um fortzufahren.
          </p>
          <div class="flex space-x-3">
            <button id="session-expired-login" class="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">
              Anmelden
            </button>
          </div>
        </div>
      </div>
    `
    
    document.body.appendChild(dialog)
    
    // Event Listener für Login-Button
    document.getElementById('session-expired-login').addEventListener('click', () => {
      window.location.href = '/users/sign_in'
    })
  }

  handleVisibilityChange() {
    if (document.hidden) {
      // Tab ist nicht aktiv - pausiere Timer
      this.pauseSessionCheck()
    } else {
      // Tab ist wieder aktiv - prüfe Session
      this.resumeSessionCheck()
    }
  }

  pauseSessionCheck() {
    this.clearSessionCheck()
  }

  resumeSessionCheck() {
    this.resetTimer()
    this.startSessionCheck()
  }

  handleBeforeUnload(event) {
    if (this.hasUnsavedChanges()) {
      event.preventDefault()
      event.returnValue = 'Sie haben ungespeicherte Änderungen. Möchten Sie die Seite wirklich verlassen?'
      return event.returnValue
    }
  }

  hasUnsavedChanges() {
    // Prüfe ob es geänderte Formulare gibt
    const changedForms = document.querySelectorAll('.backend form.form-changed')
    return changedForms.length > 0
  }

  // Public Methods für Buttons
  extendSession() {
    // Beide Warn-Zustände explizit zuruecksetzen (resetTimer wuerde bei aktiver
    // absoluter Warnung early-returnen). Das absolute Fenster wird per Heartbeat-
    // Response (absolute_remaining_ms) neu verankert.
    this.absoluteWarningActive = false
    this.warningShown = false
    this.lastActivity = Date.now()
    this.hideDialog()
    this.clearWarningTimer()

    // Sende Heartbeat an Server
    this.sendHeartbeat()
  }

  logout() {
    this.clearSessionCheck()
    this.clearWarningTimer()
    window.location.href = '/users/sign_out'
  }

  async sendHeartbeat() {
    try {
      const response = await fetch('/backend/heartbeat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) {
        throw new Error('Heartbeat failed')
      }

      // Serverseitig wurde das absolute 6h-Fenster zurueckgesetzt -> Deadline neu ankern.
      const data = await response.json().catch(() => null)
      if (data && data.absolute_remaining_ms) {
        this.absoluteDeadline = Date.now() + data.absolute_remaining_ms
        this.absoluteWarningActive = false
      }
    } catch (error) {
      console.warn('Heartbeat failed:', error)
    }
  }
}
